import React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { zoneMessages } from 'src/features/zone/utils/messages';
import CellLinkWithIcon from 'src/features/membership/components/cell/CellLinkWIthIcon';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import { useListMembershipTypeZoneRules } from 'src/features/membershipType/api/zoneRule/useListMembershipTypeZoneRules';
import CreateZoneRuleFormModal from 'src/features/membershipType/components/forms/zoneRule/CreateZoneRuleFormModal';
import CellZoneRulesActions from 'src/features/membershipType/components/cell/CellZoneRulesActions';
import DataTableColumnHeaderWrap from 'src/features/membership/components/table/DataTableColumnHeaderWrap';
import { zoneRuleStayTypeBadgeOptions } from 'src/features/membershipType/utils/constants';
import { zoneRulesMessages } from 'src/features/membershipType/utils/messages';
import { ZoneRule } from 'src/features/membershipType/types';
import { PATH } from 'src/features/navigation/utils/constants';
import {
    GeneralBlock,
    GeneralBlockBody,
    GeneralBlockHeader,
    GeneralBlockSeparator,
    GeneralBlockTitle,
} from 'src/registry/components/blocks/GeneralBlock';
import { CellBadge, DataTable, DataTableControls, DataTablePagination } from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import useSearch from 'src/registry/hooks/useSearch';
import { commonMessages, durationMessages } from 'src/utils/messages';
import { useGetMembershipType } from 'src/features/membershipType/api/useGetMembershipType';
import { useListZones } from 'src/features/zone/api/useListZones';
import { Translation } from 'src/types';
import { useParking } from 'src/providers/ParkingProvider';
import usePagination from 'src/registry/hooks/usePagination';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';

type ZoneRulesTableProps = {
    membershipTypeId: string;
    inherited?: boolean;
};
const columnHelper = createColumnHelper<ZoneRule>();

const translate = (translations: Translation[], locale: string) =>
    translations.find((translation) => translation.language === locale);

const ZoneRulesTable = ({ membershipTypeId, inherited }: ZoneRulesTableProps) => {
    const { formatMessage, locale } = useIntl();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { queryPaginationOptions, ...pagination } = usePagination();

    const { parkingId } = useParking();

    const manageMembershipType = useHasPermissions([PERMISSION.MANAGE_MEMBERSHIP_TYPES]);
    const accessZones = useHasPermissions([PERMISSION.ACCESS_ZONES]);

    const { data: listZonesData } = useListZones({ parkingId });

    const { data: membershipTemplate } = useGetMembershipType({
        parkingId,
        membershipTypeId,
    });

    const templateName = React.useMemo(() => {
        if (!membershipTemplate?.translations) return '-';
        return translate(membershipTemplate?.translations, locale)?.name ?? '-';
    }, [membershipTemplate, locale]);

    const { data: zoneRules, isLoading } = useListMembershipTypeZoneRules({
        parkingId,
        membershipTypeId,
        ...querySearchOptions,
        ...queryFiltersOptions,
        ...queryPaginationOptions,
        config: {
            enabled: !!membershipTypeId,
        },
    });

    const filteredEmptyZoneTranslations = React.useMemo(() => {
        return listZonesData?.results?.filter((zone) => zone.translations.length > 0) ?? [];
    }, [listZonesData?.results]);

    const zoneOptions = React.useMemo(
        () =>
            filteredEmptyZoneTranslations?.map((zone) => {
                const translatedZone = zone.translations.find((z) => z.language === locale);

                return { value: zone.id, label: translatedZone?.name };
            }),
        [filteredEmptyZoneTranslations, locale],
    );

    const columns = [
        columnHelper.accessor('zone', {
            header: DataTableColumnHeader,
            enableColumnFilter: true,
            cell: ({ row }) => {
                const zoneName = row.original.zone?.translations?.find(
                    (membership) => membership.language === locale,
                )?.name;
                const zoneId = row.original.zone.id;
                return (
                    <CellLinkWithIcon
                        toPath={`/${PATH.ADMINISTRATION}/zone/${zoneId}`}
                        linkTitle={zoneName}
                        hasPermissions={accessZones}
                    />
                );
            },
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(commonMessages.name),
                filterLabel: formatMessage(zoneMessages.zone),
                options: zoneOptions,
            },
        }),
        columnHelper.accessor('allowed_stay_type', {
            header: DataTableColumnHeader,
            enableColumnFilter: true,
            cell: (props) => {
                const stayType = props.row.original.allowed_stay_type;
                return stayType !== null ? <CellBadge badgeOptions={zoneRuleStayTypeBadgeOptions} {...props} /> : '-';
            },
            meta: {
                title: formatMessage(commonMessages.type),
                badgeOptions: zoneRuleStayTypeBadgeOptions,
            },
        }),
        columnHelper.accessor('transit_time_minutes', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const transitTimeMin = row.original.transit_time_minutes;
                return transitTimeMin ? formatMessage(durationMessages.pluralMinutes, { value: transitTimeMin }) : '-';
            },
            meta: {
                title: formatMessage(zoneRulesMessages.transitTime),
            },
        }),
        columnHelper.accessor('allowed_spaces_count', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(zoneRulesMessages.allowedSpacesCount),
            },
        }),
        columnHelper.accessor('reserve_spaces_count', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(zoneRulesMessages.reserveSpaces),
            },
        }),
        columnHelper.accessor('extra_visitors_count', {
            header: () => <DataTableColumnHeaderWrap title={formatMessage(membershipTypeMessages.extraVisitors)} />,
            meta: {
                title: formatMessage(membershipTypeMessages.extraVisitors),
            },
        }),
        columnHelper.accessor('allowed_extra_visitors_time_minutes', {
            header: () => (
                <DataTableColumnHeaderWrap title={formatMessage(zoneRulesMessages.allowedExtraVisitorsTime)} />
            ),
            cell: ({ row }) => {
                const extraVisitorsTime = row.original.allowed_extra_visitors_time_minutes;
                return extraVisitorsTime
                    ? formatMessage(durationMessages.pluralMinutes, {
                          value: extraVisitorsTime,
                      })
                    : '-';
            },
            meta: {
                title: formatMessage(zoneRulesMessages.allowedExtraVisitorsTime),
            },
            minSize: 180,
        }),
        ...(manageMembershipType
            ? [
                  columnHelper.display({
                      id: 'actions',
                      cell: (props) => <CellZoneRulesActions {...props} />,
                  }),
              ]
            : []),
    ];

    return (
        <>
            {membershipTemplate?.allow_custom_space_count_definition ? (
                <GeneralBlock>
                    <GeneralBlockHeader>
                        <GeneralBlockTitle className="text-lg font-semibold text-black">
                            {formatMessage(zoneRulesMessages.zoneRules)}
                        </GeneralBlockTitle>
                    </GeneralBlockHeader>
                    <GeneralBlockSeparator />
                    <GeneralBlockBody className="flex flex-col items-center p-4 align-middle">
                        <div className="text-md font-semibold text-black">
                            {formatMessage(zoneRulesMessages.zoneRulesCanNotBeDefined)}
                        </div>
                        <div className="max-w-[546px] text-sm break-words">
                            {formatMessage(zoneRulesMessages.zoneRulesCanNotBeDefinedSubtitle)}
                        </div>
                    </GeneralBlockBody>
                </GeneralBlock>
            ) : (
                <DataTable<ZoneRule, any>
                    pagination={pagination}
                    isLoading={isLoading}
                    columns={columns}
                    data={zoneRules}
                    search={search}
                    filters={filters}
                    semantics={{
                        title: formatMessage(
                            inherited ? zoneRulesMessages.inheritedZoneRules : zoneRulesMessages.zoneRules,
                            { entry: templateName },
                        ),
                        entryName: formatMessage(zoneRulesMessages.zoneRule).toLowerCase(),
                        entryNames: formatMessage(zoneRulesMessages.rules).toLowerCase(),
                        placeholder: formatMessage(commonMessages.name),
                        initialColumnVisibility: { actions: !inherited },
                    }}
                    components={{
                        controls: DataTableControls,
                        pagination: DataTablePagination,
                        ...(manageMembershipType && !inherited && { newEntry: CreateZoneRuleFormModal }),
                    }}
                    enableSearchParams={false}
                    enableLocalFilters={false}
                />
            )}
        </>
    );
};

export default ZoneRulesTable;
